import React, { useEffect, useState } from "react";
import { Heart, Loader2, Check, Award, ScrollText, HeartHandshake, Plane, Package } from "lucide-react";
import { PageHero, SectionTitle } from "@/components/Bits";
import { Reveal } from "@/components/Reveal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getProducts, startCheckout, api } from "@/lib/api";
import { IMG } from "@/lib/images";
import { toast } from "sonner";

const money = (c) => `$${(c / 100).toFixed(0)}`;

export default function Donations() {
  const [tiers, setTiers] = useState([]);
  const [patrons, setPatrons] = useState([]);
  const [wall, setWall] = useState([]);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    getProducts().then((d) => { setTiers(d.donations); setPatrons(d.patrons); });
    api.get("/hope-wall").then(({ data }) => setWall(data.patrons)).catch(() => {});
  }, []);

  const give = async (lookup_key) => {
    setBusy(lookup_key);
    try {
      const { checkout_url } = await startCheckout(lookup_key, email || null);
      window.location.href = checkout_url;
    } catch { toast.error("Could not start the gift. Please try again."); setBusy(null); }
  };

  return (
    <div data-testid="donations-page">
      <PageHero
        eyebrow="Donations & Patron Gifts"
        title="Be the reason someone stays"
        subtitle="Your generosity keeps crisis resources free, funds survivor recordings, and places brave bookmarks in children's hands. Every gift is a light."
        image={IMG.reachingHands}
        testid="donations-hero"
      />

      {/* One-time tiers */}
      <section className="max-w-7xl mx-auto px-4 py-16 sm:py-20">
        <SectionTitle eyebrow="One-Time Gift" title="Choose your impact" subtitle="Give once, generously. Here's exactly what your gift makes possible." large />
        <div className="max-w-lg mx-auto mb-12">
          <Input type="email" placeholder="Email for your receipt (optional)" value={email} onChange={(e) => setEmail(e.target.value)} className="bg-white text-center h-12 text-base" data-testid="donation-email-input" />
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {tiers.map((t, i) => (
            <Reveal key={t.lookup_key} delay={i * 80}>
              <div className="h-full bg-white rounded-3xl p-9 card-tactile flex flex-col text-center" data-testid={`donation-tier-${t.amount / 100}`}>
                <Heart className="w-11 h-11 text-bsn-gold mx-auto mb-4" />
                <p className="font-display text-5xl lg:text-6xl font-bold gold-text">{money(t.amount)}</p>
                <p className="font-serif text-2xl font-semibold text-bsn-midnight mt-3">{t.name}</p>
                <p className="text-base sm:text-lg text-bsn-sapphire/80 mt-3 leading-relaxed flex-1">{t.impact}</p>
                <Button onClick={() => give(t.lookup_key)} disabled={busy === t.lookup_key} className="mt-6 w-full h-12 text-base btn-gold font-semibold" data-testid={`donate-btn-${t.amount / 100}`}>
                  {busy === t.lookup_key ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Give {money(t.amount)}</>}
                </Button>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Patron monthly */}
      <section className="bg-bsn-midnight text-bsn-parchment">
        <div className="max-w-7xl mx-auto px-4 py-16 sm:py-20">
          <div className="text-center mb-12">
            <Award className="w-10 h-10 text-bsn-gold mx-auto mb-4" />
            <SectionTitle eyebrow="Patron Gifts · The Lamplighters" title="Join The Lamplighters" subtitle="Our monthly patrons — The Lamplighters — keep the light on so no one walks through the dark alone. You are deeply appreciated, and you may receive surprise cards in the mail and exclusive survivor stories as our thank-you." light large />
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {patrons.map((p, i) => (
              <Reveal key={p.lookup_key} delay={i * 90}>
                <div className={`h-full rounded-3xl p-7 xl:p-8 flex flex-col border-2 ${i === 1 ? "bg-bsn-navy border-bsn-gold" : "bg-bsn-navy/50 border-bsn-gold/80"}`} data-testid={`patron-tier-${p.lookup_key}`}>
                  {i === 1 && <span className="self-start text-[10px] font-mono uppercase tracking-wider px-3 py-1 rounded-full btn-gold mb-4">Most Loved</span>}
                  <h3 className="font-serif text-3xl font-semibold text-bsn-champagne">{p.name}</h3>
                  <p className="mt-2"><span className="font-display text-4xl lg:text-5xl font-bold gold-text">{money(p.amount)}</span><span className="text-bsn-ice/60 text-base">/month</span></p>
                  <ul className="mt-5 space-y-2.5 flex-1">
                    {p.perks.map((perk) => (
                      <li key={perk} className="flex items-start gap-2 text-base text-bsn-ice/85">
                        <Check className="w-5 h-5 text-bsn-gold mt-0.5 shrink-0" /> {perk}
                      </li>
                    ))}
                  </ul>
                  <Button onClick={() => give(p.lookup_key)} disabled={busy === p.lookup_key} className="mt-7 w-full h-auto min-h-12 py-3 text-base leading-snug whitespace-normal btn-gold font-semibold" data-testid={`patron-btn-${p.lookup_key}`}>
                    {busy === p.lookup_key ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Become a {p.name}</>}
                  </Button>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Where your gift goes */}
      <section className="bg-bsn-canvas border-y border-bsn-gold/20" data-testid="gift-transparency-section">
        <div className="max-w-6xl mx-auto px-4 py-16 sm:py-20">
          <SectionTitle eyebrow="Transparency" title="Where your gift goes" subtitle="Bright Stories Network shares generously with several trusted causes and partners. Your gift reaches further than our own programs alone." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: HeartHandshake, title: "Wounded Warriors", text: "Supporting veterans and their families on the road to healing and hope." },
              { icon: Heart, title: "Suicide-Prevention Materials", text: "Printing and distributing free, life-saving crisis resources into more hands." },
              { icon: Package, title: "Product Development", text: "Creating new survivor tools, Super Kids printables, and media sanctuaries." },
              { icon: Plane, title: "Speaking Engagements", text: "Travel to share the message of hope at schools, churches, and communities." },
            ].map((item, i) => (
              <Reveal key={item.title} delay={i * 80}>
                <div className="h-full bg-white rounded-2xl p-6 card-tactile text-center" data-testid={`gift-partner-${i}`}>
                  <div className="w-12 h-12 rounded-xl bg-bsn-gold/15 flex items-center justify-center mx-auto mb-4">
                    <item.icon className="w-6 h-6 text-bsn-bronze" />
                  </div>
                  <h3 className="font-serif text-lg font-semibold text-bsn-midnight">{item.title}</h3>
                  <p className="text-sm text-bsn-sapphire/75 mt-2 leading-relaxed">{item.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <p className="text-center text-xs text-bsn-sapphire/60 mt-8 max-w-2xl mx-auto">
            Bright Stories Network partners with and contributes to independent organizations. Partner names are listed to
            describe the causes we support and do not imply formal endorsement unless otherwise stated.
          </p>
        </div>
      </section>

      {/* Hope wall */}
      <section className="max-w-5xl mx-auto px-4 py-16 sm:py-20 text-center">
        <ScrollText className="w-9 h-9 text-bsn-gold mx-auto mb-4" />
        <SectionTitle eyebrow="Founder's Hope Wall" title="With gratitude to our beacons" subtitle="Patrons who help keep the light on for everyone who finds their way here." />
        <div className="flex flex-wrap justify-center gap-3" data-testid="hope-wall">
          {wall.map((name, i) => (
            <Reveal key={i} delay={i * 40}>
              <span className="px-5 py-2.5 rounded-full bg-white card-tactile font-serif text-bsn-midnight">{name}</span>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
